import React from 'react';
import { Brain, Activity, TrendingUp, Award } from 'lucide-react';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';

const Science = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <NavBar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-[#00897B] text-white pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
            The Science Behind AIMS
          </h1>
          <p className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto mb-8">
            Evidence-based neurofeedback technology validated by decades of peer-reviewed research
          </p>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-[#00897B]">50+</div>
              <div className="text-sm text-gray-200 mt-2">Years of Research</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-[#00897B]">2,000+</div>
              <div className="text-sm text-gray-200 mt-2">Published Studies</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-[#00897B]">Level 1</div>
              <div className="text-sm text-gray-200 mt-2">Clinical Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-grow py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Measurements Section */}
          <div className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="order-2 md:order-1">
                <div className="inline-flex items-center gap-2 bg-[#00897B]/10 text-[#00897B] px-4 py-2 rounded-full mb-6">
                  <Activity className="w-5 h-5" />
                  <span className="text-sm font-medium">EEG Technology</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Precise Measurements
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#00897B] rounded-lg flex items-center justify-center mt-1">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Brain labs and neurofeedback clinics have long employed EEG for the purposes of assessing cognitive functions, sleep, and even retraining the brain.
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#00897B] rounded-lg flex items-center justify-center mt-1">
                      <Activity className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      EEG electrodes are placed on precise areas of the scalp. This plays a significant role in giving meaning to the generated signals.
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#00897B] rounded-lg flex items-center justify-center mt-1">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      To achieve the same placement flexibility and accuracy in interpreting the signals, AIMS adds an external gold-cup electrode to the Muse headset
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Image - Brain */}
              <div className="flex justify-center order-1 md:order-2">
                <div className="relative bg-gradient-to-br from-[#00897B]/10 to-gray-100 rounded-3xl p-8">
                  <img
                    src="/brain-temporal.png"
                    alt="Brain Temporal Area"
                    className="w-full max-w-md h-auto drop-shadow-2xl"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Research Foundation Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Research Foundation
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Backed by rigorous scientific evidence and clinical validation
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Research Card 1 */}
              <div className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-3xl p-8 hover:border-[#00897B] hover:shadow-2xl transition-all duration-300">
                <div className="w-14 h-14 bg-[#00897B]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00897B] transition-colors">
                  <TrendingUp className="w-7 h-7 text-[#00897B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Neurofeedback Efficacy
                </h3>
                <p className="text-gray-700 leading-relaxed text-base">
                  Over 2,000 peer-reviewed studies demonstrate the effectiveness of neurofeedback training for various conditions including ADHD, anxiety, depression, PTSD, and peak performance enhancement. Meta-analyses consistently show significant improvements in attention, emotional regulation, and cognitive function.
                </p>
              </div>

              {/* Research Card 2 */}
              <div className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-3xl p-8 hover:border-[#00897B] hover:shadow-2xl transition-all duration-300">
                <div className="w-14 h-14 bg-[#00897B]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00897B] transition-colors">
                  <Brain className="w-7 h-7 text-[#00897B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Brain Plasticity
                </h3>
                <p className="text-gray-700 leading-relaxed text-base">
                  Neuroplasticity research shows that the brain can reorganize itself by forming new neural connections throughout life. Neurofeedback leverages this principle by providing real-time feedback to help the brain learn more efficient patterns of activation, leading to lasting changes in brain function.
                </p>
              </div>

              {/* Research Card 3 */}
              <div className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-3xl p-8 hover:border-[#00897B] hover:shadow-2xl transition-all duration-300">
                <div className="w-14 h-14 bg-[#00897B]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00897B] transition-colors">
                  <Award className="w-7 h-7 text-[#00897B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Clinical Validation
                </h3>
                <p className="text-gray-700 leading-relaxed text-base">
                  The American Academy of Pediatrics has rated neurofeedback as a Level 1 ("Best Support") intervention for ADHD. Multiple randomized controlled trials have demonstrated its efficacy comparable to or exceeding traditional interventions, with the added benefit of no side effects.
                </p>
              </div>

              {/* Research Card 4 */}
              <div className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-3xl p-8 hover:border-[#00897B] hover:shadow-2xl transition-all duration-300">
                <div className="w-14 h-14 bg-[#00897B]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00897B] transition-colors">
                  <Activity className="w-7 h-7 text-[#00897B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Peak Performance
                </h3>
                <p className="text-gray-700 leading-relaxed text-base">
                  Research with elite athletes, musicians, and executives shows that neurofeedback training can enhance focus, reduce performance anxiety, and improve flow states. Studies demonstrate measurable improvements in reaction time, decision-making, and sustained attention.
                </p>
              </div>
            </div>
          </div>

          {/* How It Works Section */}
          <div className="mb-20">
            <div className="bg-gradient-to-br from-[#C8E6E1] to-[#00897B]/20 rounded-3xl p-8 sm:p-10 md:p-12 shadow-xl">
              <div className="text-center mb-10">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  How Neurofeedback Training Works
                </h2>
                <p className="text-xl text-gray-700">
                  A proven 4-step process for brain optimization
                </p>
              </div>
              <div className="space-y-8 text-lg text-gray-700">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#00897B] text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Assessment</h3>
                    <p>A comprehensive P&ID brain map identifies specific patterns of dysregulation and areas for optimization.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#00897B] text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Protocol Design</h3>
                    <p>Based on your brain map and goals, we create a personalized training protocol targeting specific brainwave frequencies and brain regions.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#00897B] text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Training Sessions</h3>
                    <p>During sessions, your brain receives real-time audio and visual feedback, learning to produce healthier patterns through operant conditioning.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#00897B] text-white rounded-full flex items-center justify-center font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Progress Tracking</h3>
                    <p>We monitor changes in brain activity and correlate them with improvements in symptoms and performance metrics.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Benefits Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Evidence-Based Benefits
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Why thousands choose neurofeedback for brain wellness
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 sm:p-10 md:p-12 border-2 border-gray-200 shadow-xl">
              <ul className="grid md:grid-cols-2 gap-6 text-lg text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-[#00897B] text-2xl">✓</span>
                  <span><strong>Non-invasive and medication-free:</strong> No drugs, no side effects, just natural brain training</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00897B] text-2xl">✓</span>
                  <span><strong>Long-lasting results:</strong> Changes in brain patterns can persist long after training ends</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00897B] text-2xl">✓</span>
                  <span><strong>Personalized approach:</strong> Every training protocol is customized to your unique brain patterns</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00897B] text-2xl">✓</span>
                  <span><strong>Measurable outcomes:</strong> Track objective changes in brain function over time</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00897B] text-2xl">✓</span>
                  <span><strong>Complementary to other treatments:</strong> Can be used alongside therapy, medication, or lifestyle changes</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00897B] text-2xl">✓</span>
                  <span><strong>Backed by decades of research:</strong> Supported by over 40 years of scientific studies</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-[#00897B] rounded-3xl p-12 sm:p-16 shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#00897B]/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

            <div className="relative text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6">
                <Brain className="w-5 h-5" />
                <span className="text-sm font-medium">Transform Your Brain Health</span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                Experience the Science of<br />Brain Optimization
              </h2>

              <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto">
                Start your journey to better brain health with our evidence-based neurofeedback protocols
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/register"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00897B] hover:bg-[#00796B] text-white rounded-full text-lg font-medium transition-all hover:scale-105 shadow-lg"
                >
                  Get Started Today
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
                <a
                  href="/lbw-updates"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 rounded-full text-lg font-medium transition-all hover:scale-105 shadow-lg"
                >
                  Learn How It Works
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Science;
